"use client"

import { useState, useEffect } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import "react-pdf/dist/esm/Page/TextLayer.css"
import Button from "./Button"
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react"
import { Card, CardContent } from "./card"

// Configure pdfjs worker
try {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  console.log("PDF.js worker configured with version:", pdfjs.version);
} catch (error) {
  console.error("Failed to configure PDF.js worker:", error);
}

interface PDFViewerProps {
  pdfUrl: string | null
}

const PDFViewer = ({ pdfUrl }: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  // Use a key to force re-render of the PDF when the URL changes
  const [pdfKey, setPdfKey] = useState<string>('pdf-1')
  
  // Update key when PDF URL changes to force re-render
  useEffect(() => {
    if (pdfUrl) {
      // Generate a new key based on URL and timestamp to force re-render
      console.log("PdfViewer: PDF URL changed, updating key", pdfUrl);
      setPdfKey(`pdf-${Date.now()}`);
    }
  }, [pdfUrl])

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    console.log("PDF document loaded successfully with", numPages, "pages");
    setNumPages(numPages);
    setPageNumber(1); // Reset to first page when a new document loads
  }

  function changePage(offset: number) {
    setPageNumber((prevPageNumber) => {
      const newPageNumber = prevPageNumber + offset
      if (numPages && newPageNumber >= 1 && newPageNumber <= numPages) {
        return newPageNumber
      }
      return prevPageNumber
    })
  }

  function zoomIn() {
    setScale((prevScale) => Math.min(prevScale + 0.2, 2.0))
  }

  function zoomOut() {
    setScale((prevScale) => Math.max(prevScale - 0.2, 0.6))
  }

  if (!pdfUrl) {
    return (
      <Card className="mt-4 w-full">
        <CardContent className="flex items-center justify-center h-60 text-muted-foreground">
          No resume uploaded yet
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="mt-4 w-full">
      <CardContent className="p-4 overflow-auto">
        <div className="flex flex-col items-center">
          <Document
            key={pdfKey} // Add key to force re-render when URL changes
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error("Error loading PDF document:", error);
            }}
            loading={
              <div className="h-96 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                <span>Loading PDF...</span>
              </div>
            }
            error={
              <div className="h-60 flex flex-col items-center justify-center text-red-500 p-4">
                <p className="font-medium mb-2">Failed to load PDF.</p>
                <p className="text-sm mb-4">The file might not be a valid PDF, or the link may have expired.</p>
                <p className="text-xs mb-4">{pdfUrl?.substring(0, 50) + (pdfUrl && pdfUrl.length > 50 ? "..." : "")}</p>
                <button 
                  onClick={() => {
                    // Force reload the current page to refresh everything
                    window.location.reload();
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Refresh Page
                </button>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              className="border shadow-sm mb-4" // Added bottom margin for spacing
              width={800} // Set a max width to ensure consistency
              error={
                <div className="h-60 flex flex-col items-center justify-center text-red-400 p-4">
                  <p>Error rendering page {pageNumber}.</p>
                </div>
              }
              onRenderError={(error) => {
                console.error(`Error rendering page ${pageNumber}:`, error);
              }}
            />
          </Document>

          <div className="flex items-center justify-between w-full mt-4 mb-2 sticky bottom-0 bg-white py-2">
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => changePage(-1)}
                disabled={pageNumber <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center px-2">
                Page {pageNumber} of {numPages || "-"}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => changePage(1)}
                disabled={numPages !== null && pageNumber >= numPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={zoomOut}
                disabled={scale <= 0.6}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={zoomIn}
                disabled={scale >= 2.0}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default PDFViewer
